import type { PermissionAction, ScreenId } from "@agentcall/core/type/permission_type"
import { useAtomValue } from "jotai"
import { useMemo } from "react"
import { meApi } from "@/api/client/me"
import { isSessionValidAtom, sessionUserAtom } from "@/store/session"

/** サーバーが解決した画面権限を参照する。 */
export function usePermission() {
  const isSessionValid = useAtomValue(isSessionValidAtom)
  const sessionUser = useAtomValue(sessionUserAtom)

  const { data, isPending, isError, refetch } = meApi.useMePermissionList({
    query: {
      enabled: isSessionValid,
      // 別ユーザーの権限キャッシュを再利用しない。
      queryKey: [...meApi.getMePermissionListQueryKey(), sessionUser?.id],
      // 他の管理者によるロール変更を反映する。
      staleTime: 30 * 1000,
      refetchOnWindowFocus: "always",
    },
  })

  const isLoading = isSessionValid && isPending

  return useMemo(() => {
    const screens = data?.screens ?? []
    const byScreenId = new Map(screens.map((screen) => [screen.screenId, screen]))

    const hasPermission = (screenId: ScreenId, action: PermissionAction) =>
      byScreenId.get(screenId)?.actions.includes(action) ?? false

    return {
      can: hasPermission,
      canAccess: (screenId: ScreenId): boolean => hasPermission(screenId, "read"),
      canCreate: (screenId: ScreenId) => hasPermission(screenId, "create"),
      canUpdate: (screenId: ScreenId) => hasPermission(screenId, "update"),
      canDelete: (screenId: ScreenId) => hasPermission(screenId, "delete"),
      role: data?.role,

      roleLabel: data?.roleLabel,

      organizationId: data?.organizationId,

      isLoading,

      isError,

      refetch,
    }
  }, [data, isLoading, isError, refetch])
}
